import { defineStore } from 'pinia'
import { mergeWith } from 'lodash-unified'
import { HttpClient } from '@/http/httpClient'
import type { WantItAuthorBenefitConfig } from '@/views/AppChallengeDetail/components/CampaignProductPanel/types'
const http = new HttpClient()

export interface ActivityConfig {
  uniqueTopicId: string
  excludeActions?: string[]
  [key: string]: any
}

interface Config {
  cardDisplayCount: number // 帖子详情页初始推荐卡片露出个数
  guideAwardImageList: string[] // 新手引导奖励图片列表
  feSettings: {
    marketActivityConfig?: string[]
    wantItAuthorBenefit?: WantItAuthorBenefitConfig
    wantItDiscount?: number // 想要按钮折扣，0~1，如 0.9 表示 9折，不下发时默认 0.9
    showBlindBoxEntry?: boolean // 首页盲盒快捷入口，不下发时默认 true（展示），后端下发 false 才关闭
    blindBoxEntryWhitelist?: string[] // showBlindBoxEntry 关闭时，仍可看到盲盒入口的用户 UID 白名单
    [key: string]: any
  }
  feedBannerList: Array<{
    banner_id: string
    banner_image_url: string
    link_url: string
    start_time: string
    end_time: string
  }> // 首页banner列表
}

interface RequestOptions {
  retry: number
}
const maxRetry = 3

export const useGlobalConfigStore = defineStore('globalConfig', {
  state: (): Config => ({
    cardDisplayCount: 3, // 只有本地没有持久化的时候兜底值才会生效
    guideAwardImageList: [],
    feedBannerList: [],
    feSettings: {}
  }),
  getters: {
    // activeFeedBannerList: state =>
    //   state.feedBannerList.filter(item => {
    //     const endTime = new Date(item.end_time.replace(/-/g, '/')).getTime()
    //     const startTime = new Date(item.start_time.replace(/-/g, '/')).getTime()
    //     return Date.now() >= startTime && Date.now() <= endTime
    //   })

    /**
     * 根据 topicId 返回对应营销活动的 excludeActions，未匹配返回空数组
     */
    getExcludedActionsByTopicId:
      state =>
      (topicId?: string): string[] => {
        if (!topicId) return []
        const { feSettings } = state
        const marketActivityConfig: string[] = feSettings?.marketActivityConfig ?? []
        for (const activityKey of marketActivityConfig) {
          const activity: ActivityConfig | undefined = feSettings?.[activityKey]
          if (activity?.uniqueTopicId === topicId) {
            return activity.excludeActions ?? []
          }
        }
        return []
      },
    wantItAuthorBenefit: state => {
      return state.feSettings?.wantItAuthorBenefit
    },
    wantItDiscount: state => (state.feSettings?.wantItDiscount as number) ?? 0.9,
    // 首页盲盒快捷入口显示控制，后端未下发时默认展示，后端下发 false 才关闭
    showBlindBoxEntry: state => state.feSettings?.showBlindBoxEntry !== false,
    // showBlindBoxEntry 关闭时，仍可看到盲盒入口的用户 UID 白名单
    blindBoxEntryWhitelist: state => state.feSettings?.blindBoxEntryWhitelist ?? []
  },
  actions: {
    async fetchConfig(opt: RequestOptions = { retry: maxRetry }) {
      try {
        const resultList = await Promise.allSettled([
          http.get('/aigc/api/fe-settings/all-config', {}, opt),
          http.get(
            '/aigc/api/activity/want-it/guide',
            {},
            {
              isShowErrorModal: false
            }
          )
        ])
        if (resultList[0].status === 'fulfilled') {
          const { pc = {}, common = {} } = resultList[0]?.value?.data ?? {}

          // 默认值兜底 → common → pc 依次覆盖；遇到数组直接整体替换，不做深度合并
          const feSettings = mergeWith({}, this.feSettings, common, pc, (_objValue, srcValue) => {
            if (Array.isArray(srcValue)) return srcValue
          })
          this.cardDisplayCount = feSettings.cardDisplayCount
          this.feSettings = feSettings
        }
        if (resultList[1].status === 'fulfilled') {
          this.guideAwardImageList = resultList[1].value.data.productImage
        }
      } catch (err) {
        // empty
      }
    }
  },
  persist: true // 开启持久化
})
